// Contenido del carrusel de bienvenida (pre-registro). El **copy** base sale del frame de
// Figma "Carosel" (node 148:451) y es el del set general; cada rubro puede tener sus propios
// **fondos** (imágenes/videos) y su propio **copy** (ADR-799). El set general vive en
// `public/images/onboarding`; los sets por rubro en `public/images/onboarding/rubros/<rubro>/carousel`
// (ver ADR-695 y ADR-785). Todo se referencia por URL absoluta, igual que el resto de assets.

export interface OnboardingSlide {
  /** Título principal (Heading / Extra Large strong). */
  title: string;
  /** Bajada (Body / Small strong). */
  description: string;
  /** Fondo full-bleed a **resolución original** (servido desde `public/`). */
  image: string;
  /**
   * Versión **optimizada** (más liviana, ~500px) del mismo fondo. Se muestra al
   * instante y se mantiene hasta que carga la original, que la reemplaza de forma
   * imperceptible (ver `OnboardingCarousel`, ADR-742).
   */
  preview: string;
  /** Alt del fondo (descriptivo, para lectores de pantalla). */
  alt: string;
  /**
   * Fondo en **video** (MP4, resolución original) opcional. Si está presente, el
   * carrusel reproduce el video (autoplay/muted/loop) sobre el póster; `image`/`preview`
   * hacen de póster y fallback — incl. `prefers-reduced-motion`, donde el video NO se
   * reproduce. ADR-758.
   */
  video?: string;
  /**
   * Versión **optimizada** (liviana, ~540px) del video. Se reproduce al instante y se
   * reemplaza por `video` (original) cuando éste termina de bufferear, de forma
   * imperceptible — mismo patrón progresivo que `preview` para las imágenes. ADR-761.
   */
  videoPreview?: string;
  /**
   * Overrides de fondo para **desktop** (≥1024px, breakpoint `lg`). Lo que no se define
   * acá cae al set base (que es el de **mobile**). Sirve para los rubros que tienen un
   * asset propio por formato: en mobile el encuadre es vertical (9:16) y en desktop la
   * imagen vive en un panel de media pantalla, más cuadrado. ADR-795.
   */
  desktop?: Partial<
    Pick<
      OnboardingSlide,
      'image' | 'preview' | 'alt' | 'video' | 'videoPreview' | 'videoPosition' | 'imagePosition'
    >
  >;
  /**
   * `object-position` del **video** de la slide (no de la imagen). Default: `top` (el
   * fondo se ancla arriba). Con `center bottom` el video queda centrado horizontalmente
   * y anclado al pie — útil cuando lo importante del plano está abajo. ADR-795.
   */
  videoPosition?: string;
  /**
   * `object-position` de la **imagen** de la slide (fondo y póster). Default: `top`.
   * En una slide con video conviene setear los dos con el mismo valor, así el póster y el
   * video quedan en el mismo encuadre y el swap no da un salto. ADR-798.
   */
  imagePosition?: string;
}

/** Título + bajada de una slide. */
type SlideCopy = Pick<OnboardingSlide, 'title' | 'description'>;

/**
 * Copy de valor de cada slide — set **general** (`/bienvenida`). Es el default y **no se
 * toca**: los rubros que quieran otra voz definen el suyo en `SLIDE_COPY_BY_RUBRO`
 * (ADR-799). Un rubro sin copy propio hereda éste.
 */
const SLIDE_COPY: SlideCopy[] = [
  {
    title: 'Fotos profesionales',
    description:
      'Subís la foto de tu producto y en segundos tenés una producción de fotos profesionales lista para vender. Sin fotógrafo, sin estudio.',
  },
  {
    title: 'Un producto, muchas fotos',
    description:
      'De una sola foto sacás todas las que necesites: distintos fondos, ángulos y escenas. Sin volver a armar una producción cada vez.',
  },
  {
    title: 'Videos para tus redes',
    description:
      'No solo fotos: creá videos con influencers virtuales para Reels y TikTok. De la foto al video, en la misma app.',
  },
  {
    title: 'Avatares para tu producto',
    description:
      'Poné tu producto sobre avatares virtuales. Guardá los que más te gustan y repetilos en cada colección.',
  },
];

/**
 * Copy **por rubro** (ADR-799). Mismas 4 promesas que el general —fotos profesionales /
 * muchas variantes de una sola foto / video / avatares (modelos en Moda)— pero dichas en el vocabulario del
 * oficio: lo que sube ese usuario ("tu plato", "tu prenda"), lo que se ahorra ("sin volver
 * a preparar el plato") y dónde lo usa (menú, delivery, colección). El general
 * (`/bienvenida`) queda **exactamente** como estaba; un rubro sin entrada acá hereda ese
 * copy. Se mantiene el largo de cada campo para no romper el layout (título de 1 línea en
 * mobile, bajada de 2–3).
 */
const SLIDE_COPY_BY_RUBRO: Record<string, SlideCopy[]> = {
  // HOGAR Y DECORACIÓN — tiendas de muebles y deco. El dolor propio del rubro es la
  // **ambientación**: el producto se vende en contexto y montar la escena cuesta plata.
  hogar: [
    {
      title: 'Tu producto, ambientado',
      description:
        'Subís la foto de tu mueble y en segundos lo tenés ambientado como en una producción de deco. Sin alquilar un set ni mover nada de lugar.',
    },
    {
      title: 'Un mueble, todos los ambientes',
      description:
        'De una sola foto sacás living, dormitorio o exterior, en el estilo que quieras. Sin volver a armar la escena cada vez.',
    },
    {
      title: 'Videos para tus redes',
      description:
        'No solo fotos: creá videos que recorren tus ambientes para Reels y TikTok. De la foto al video, en la misma app.',
    },
    {
      title: 'Escenas con personas',
      description:
        'Sumá personas usando tus muebles: dan escala y muestran cómo se vive tu producto. Guardá las escenas que funcionan y repetilas.',
    },
  ],

  // MODA E INDUMENTARIA — marcas de ropa. Hablan de **campaña**, **look** y **colección**;
  // el costo real es la producción con modelo + estudio en cada temporada.
  moda: [
    {
      title: 'Campañas sin estudio',
      description:
        'Subís la foto de tu prenda y en segundos tenés una campaña lista para vender. Sin fotógrafo, sin alquilar estudio.',
    },
    {
      title: 'Una prenda, todos los looks',
      description:
        'De una sola foto sacás todos los looks que necesites: fondos, ángulos y climas distintos. Sin repetir la producción en cada colección.',
    },
    {
      title: 'Reels para tu marca',
      description:
        'No solo fotos: creá videos con modelos virtuales para Reels y TikTok. De la percha al video, en la misma app.',
    },
    {
      title: 'Modelos para tus prendas',
      description:
        'Poné tus prendas sobre modelos virtuales, con el estilo que busca tu marca. Guardá los que más te gustan y repetilos en cada colección.',
    },
  ],

  // BELLEZA Y CUIDADO PERSONAL — la categoría se compra por **estética de marca** y por
  // ver el producto **aplicado** (textura, piel, tono). Packshot impecable + prueba real.
  belleza: [
    {
      title: 'Fotos con estética de marca',
      description:
        'Subís la foto de tu producto y en segundos tenés una producción con el nivel de una marca grande. Sin fotógrafo, sin estudio.',
    },
    {
      title: 'Un producto, mil escenas',
      description:
        'De una sola foto sacás todas las que necesites: texturas, fondos y climas distintos. Sin volver a montar la escena cada vez.',
    },
    {
      title: 'Videos para tus redes',
      description:
        'No solo fotos: creá videos de aplicación y textura para Reels y TikTok. Del packshot al video, en la misma app.',
    },
    {
      title: 'Avatares para tu producto',
      description:
        'Mostrá tu producto en piel real con avatares virtuales: distintos tonos, edades y estilos. Guardá los que van con tu marca y repetilos.',
    },
  ],

  // GASTRONOMÍA — restaurantes y delivery. Deciden por **apetito** y el plato se arruina:
  // no se puede repetir la toma. El destino es el menú, la app de delivery y las redes.
  gastronomia: [
    {
      title: 'Fotos que dan hambre',
      description:
        'Subís la foto de tu plato y en segundos tenés una producción lista para el menú y el delivery. Sin fotógrafo, sin estudio.',
    },
    {
      title: 'Un plato, muchas fotos',
      description:
        'De una sola foto sacás todas las que necesites: mesas, fondos y climas distintos. Sin volver a preparar el plato para cada toma.',
    },
    {
      title: 'Videos para tus redes',
      description:
        'No solo fotos: creá videos de tus platos para Reels y TikTok. Del mostrador al video, en la misma app.',
    },
    {
      title: 'Tus platos en escena',
      description:
        'Sumá personas disfrutando tus platos: dan contexto y hacen que se vea más rico. Guardá las escenas que funcionan y repetilas.',
    },
  ],
};

/** Fondos de una slide (imagen/preview/alt + video, overrides de desktop), propios de cada set. */
type SlideMedia = Pick<
  OnboardingSlide,
  | 'image'
  | 'preview'
  | 'alt'
  | 'video'
  | 'videoPreview'
  | 'desktop'
  | 'videoPosition'
  | 'imagePosition'
>;

// ── Set GENERAL (default, cualquier usuario) — `/bienvenida`. ──
const GENERAL_MEDIA: SlideMedia[] = [
  {
    image: '/images/onboarding/step-1.jpg',
    preview: '/images/onboarding/step-1-preview.jpg',
    alt: 'Modelo con un vestido claro en una toma editorial',
    video: '/images/onboarding/step-1.mp4',
    videoPreview: '/images/onboarding/step-1-preview.mp4',
  },
  {
    image: '/images/onboarding/step-2.jpg',
    preview: '/images/onboarding/step-2-preview.jpg',
    alt: 'Escultura blanca suspendida en un ambiente oscuro',
  },
  {
    image: '/images/onboarding/step-3.jpg',
    preview: '/images/onboarding/step-3-preview.jpg',
    alt: 'Labial rojo sobre una superficie de mármol con luz cálida',
    video: '/images/onboarding/step-3.mp4',
    videoPreview: '/images/onboarding/step-3-preview.mp4',
  },
  {
    image: '/images/onboarding/step-4.jpg',
    preview: '/images/onboarding/step-4-preview.jpg',
    alt: 'Persona comiendo una hamburguesa en un café',
  },
];

// ── Set HOGAR Y DECORACIÓN — `/bienvenida/hogar`. ──
// Mismos copies; fondos propios del rubro en `public/images/onboarding/rubros/hogar/carousel/`
// (ver README de esa carpeta). Slides 1 y 3 son **video** (como el general); 2 y 4, imagen.
const HOGAR_MEDIA: SlideMedia[] = [
  {
    image: '/images/onboarding/rubros/hogar/carousel/step-1.jpg',
    preview: '/images/onboarding/rubros/hogar/carousel/step-1-preview.jpg',
    alt: 'Lámpara de diseño con forma de flor sobre una mesa de mármol en un vestidor',
    video: '/images/onboarding/rubros/hogar/carousel/step-1.mp4',
    videoPreview: '/images/onboarding/rubros/hogar/carousel/step-1-preview.mp4',
  },
  {
    // **Prueba (ADR-796):** el asset de desktop se usa TAMBIÉN en mobile → una sola
    // imagen para los dos formatos (por eso no hay bloque `desktop`). El original de
    // mobile sigue en la carpeta como `step-2.jpg` / `step-2-preview.jpg` por si se vuelve.
    image: '/images/onboarding/rubros/hogar/carousel/step-2-desktop.jpg',
    preview: '/images/onboarding/rubros/hogar/carousel/step-2-desktop-preview.jpg',
    alt: 'Sillón bouclé color salmón con una mano apoyada en el respaldo',
  },
  {
    image: '/images/onboarding/rubros/hogar/carousel/step-3.jpg',
    preview: '/images/onboarding/rubros/hogar/carousel/step-3-preview.jpg',
    alt: 'Mesa de luz con un florero de flores secas y libros, con luz cálida',
    video: '/images/onboarding/rubros/hogar/carousel/step-3.mp4',
    videoPreview: '/images/onboarding/rubros/hogar/carousel/step-3-preview.mp4',
    // Único caso con anclaje al pie: el plano importante de este video está abajo. ADR-795.
    videoPosition: 'center bottom',
  },
  {
    image: '/images/onboarding/rubros/hogar/carousel/step-4.jpg',
    preview: '/images/onboarding/rubros/hogar/carousel/step-4-preview.jpg',
    alt: 'Persona junto a un espejo de cuerpo entero ondulado en un dormitorio',
  },
];

// ── Sets por rubro. `hogar` y `moda` cargados; `gastronomia` casi (slide 1 pendiente);
// `belleza` con carpeta + README, esperando assets (sus `alt` son provisorios). Cuando
// llegan los originales se procesan igual que Hogar y, si una slide es video (típicamente
// 1 y 3, como el general), se le agregan `video`/`videoPreview`. ──

// MODA E INDUMENTARIA — `/bienvenida/moda`.
// Slide 1 **reutiliza el video del carrusel general** (`/images/onboarding/step-1.*`, pedido
// del usuario). Slide 3 = video propio; 2 y 4 = imagen.
const MODA_MEDIA: SlideMedia[] = [
  {
    image: '/images/onboarding/step-1.jpg',
    preview: '/images/onboarding/step-1-preview.jpg',
    alt: 'Modelo con un vestido claro en una toma editorial',
    video: '/images/onboarding/step-1.mp4',
    videoPreview: '/images/onboarding/step-1-preview.mp4',
  },
  {
    // Mobile mantiene el asset original; desktop usa el suyo propio. ADR-795.
    image: '/images/onboarding/rubros/moda/carousel/step-2.jpg',
    preview: '/images/onboarding/rubros/moda/carousel/step-2-preview.jpg',
    alt: 'Camisa marrón texturada sobre un banco de hormigón junto a un ventanal',
    desktop: {
      image: '/images/onboarding/rubros/moda/carousel/step-2-desktop.jpg',
      preview: '/images/onboarding/rubros/moda/carousel/step-2-desktop-preview.jpg',
    },
  },
  {
    image: '/images/onboarding/rubros/moda/carousel/step-3.jpg',
    preview: '/images/onboarding/rubros/moda/carousel/step-3-preview.jpg',
    alt: 'Mujer con tapado de piel, pañuelo de seda y guantes frente a una vitrina',
    video: '/images/onboarding/rubros/moda/carousel/step-3.mp4',
    videoPreview: '/images/onboarding/rubros/moda/carousel/step-3-preview.mp4',
  },
  {
    image: '/images/onboarding/rubros/moda/carousel/step-4.jpg',
    preview: '/images/onboarding/rubros/moda/carousel/step-4-preview.jpg',
    alt: 'Persona con jeans anchos y remera negra en un andén de subte',
  },
];

// BELLEZA Y CUIDADO PERSONAL — `/bienvenida/belleza`.
// Slide 1 = video propio; 2 y 4 = imagen propia; **slide 3 reutiliza la del carrusel
// general** (el video del labial, `/images/onboarding/step-3.*`) — pedido del usuario,
// mismo patrón que la slide 1 de moda. Sin assets de desktop: el set base sirve para los
// dos formatos (ADR-797).
const BELLEZA_MEDIA: SlideMedia[] = [
  {
    image: '/images/onboarding/rubros/belleza/carousel/step-1.jpg',
    preview: '/images/onboarding/rubros/belleza/carousel/step-1-preview.jpg',
    alt: 'Producto de belleza en una toma profesional',
    video: '/images/onboarding/rubros/belleza/carousel/step-1.mp4',
    videoPreview: '/images/onboarding/rubros/belleza/carousel/step-1-preview.mp4',
    // Centrado **sólo en desktop** (en mobile sigue anclado arriba). Video y póster con el
    // mismo encuadre para que el swap no salte. ADR-798.
    desktop: { videoPosition: 'center', imagePosition: 'center' },
  },
  {
    image: '/images/onboarding/rubros/belleza/carousel/step-2.jpg',
    preview: '/images/onboarding/rubros/belleza/carousel/step-2-preview.jpg',
    alt: 'Producto de cuidado personal en distintas escenas',
    // Centrado sólo en desktop (ADR-798).
    desktop: { imagePosition: 'center' },
  },
  {
    // Reutiliza el set general (labial sobre mármol) — no se copian archivos.
    image: '/images/onboarding/step-3.jpg',
    preview: '/images/onboarding/step-3-preview.jpg',
    alt: 'Labial rojo sobre una superficie de mármol con luz cálida',
    video: '/images/onboarding/step-3.mp4',
    videoPreview: '/images/onboarding/step-3-preview.mp4',
  },
  {
    image: '/images/onboarding/rubros/belleza/carousel/step-4.jpg',
    preview: '/images/onboarding/rubros/belleza/carousel/step-4-preview.jpg',
    alt: 'Producto de belleza presentado por un avatar',
  },
];

// GASTRONOMÍA — `/bienvenida/gastronomia`.
// Slides 1 y 3 son **video**; 2 (pizza) y 4 (hamburguesa), imagen. La slide 1 tiene video
// propio por formato (vertical en mobile, cuadrado en desktop). ADR-795.
const GASTRONOMIA_MEDIA: SlideMedia[] = [
  {
    image: '/images/onboarding/rubros/gastronomia/carousel/step-1.jpg',
    preview: '/images/onboarding/rubros/gastronomia/carousel/step-1-preview.jpg',
    alt: 'Plato gastronómico en una toma profesional',
    video: '/images/onboarding/rubros/gastronomia/carousel/step-1.mp4',
    videoPreview: '/images/onboarding/rubros/gastronomia/carousel/step-1-preview.mp4',
    desktop: {
      image: '/images/onboarding/rubros/gastronomia/carousel/step-1-desktop.jpg',
      preview: '/images/onboarding/rubros/gastronomia/carousel/step-1-desktop-preview.jpg',
      video: '/images/onboarding/rubros/gastronomia/carousel/step-1-desktop.mp4',
      videoPreview: '/images/onboarding/rubros/gastronomia/carousel/step-1-desktop-preview.mp4',
    },
  },
  {
    image: '/images/onboarding/rubros/gastronomia/carousel/step-2.jpg',
    preview: '/images/onboarding/rubros/gastronomia/carousel/step-2-preview.jpg',
    alt: 'Pizza napolitana con jamón crudo y rúcula en una mesa de restaurante',
  },
  {
    // Pasó de imagen a **video** (ADR-795); el póster es el primer frame del propio video.
    image: '/images/onboarding/rubros/gastronomia/carousel/step-3.jpg',
    preview: '/images/onboarding/rubros/gastronomia/carousel/step-3-preview.jpg',
    alt: 'Plato gastronómico en movimiento, para redes',
    video: '/images/onboarding/rubros/gastronomia/carousel/step-3.mp4',
    videoPreview: '/images/onboarding/rubros/gastronomia/carousel/step-3-preview.mp4',
    // Centrado (en vez del `top` por defecto): el plano vive en el medio. ADR-796.
    videoPosition: 'center',
  },
  {
    image: '/images/onboarding/rubros/gastronomia/carousel/step-4.jpg',
    preview: '/images/onboarding/rubros/gastronomia/carousel/step-4-preview.jpg',
    alt: 'Hamburguesa con cheddar sobre una tabla de madera',
  },
];

/**
 * Une un copy con un set de fondos → slides completas del carrusel. Sin `copy` usa el
 * **general** (ADR-799).
 */
function buildSlides(media: SlideMedia[], copy: SlideCopy[] = SLIDE_COPY): OnboardingSlide[] {
  return copy.map((c, i) => ({ ...c, ...media[i] }));
}

/**
 * Slides del carrusel de bienvenida — set **general** (default). Se mantiene el nombre
 * `ONBOARDING_SLIDES` para no romper consumidores existentes.
 */
export const ONBOARDING_SLIDES: OnboardingSlide[] = buildSlides(GENERAL_MEDIA);

/**
 * Fondos por **rubro** (clave = id de rubro de `SETUP_CATEGORIES`, p.ej. `hogar`). Cada rubro
 * tiene sus fondos (y, si está en `SLIDE_COPY_BY_RUBRO`, su copy). Los 4 rubros tienen assets propios
 * (belleza se sumó en ADR-797).
 */
const CAROUSEL_MEDIA_BY_RUBRO: Record<string, SlideMedia[]> = {
  hogar: HOGAR_MEDIA,
  moda: MODA_MEDIA,
  gastronomia: GASTRONOMIA_MEDIA,
  belleza: BELLEZA_MEDIA,
};

/** Rubros que ya tienen carrusel propio (para el routing `/bienvenida/:rubro`). */
export const CAROUSEL_RUBROS = Object.keys(CAROUSEL_MEDIA_BY_RUBRO);

/**
 * Slides del carrusel para un rubro dado (id de `SETUP_CATEGORIES`, p.ej. `hogar`): sus
 * fondos + su copy. Si el rubro es desconocido o todavía no tiene set propio, cae al
 * **general** (y un rubro con fondos propios pero sin copy propio hereda el copy general).
 */
export function getCarouselSlides(rubro?: string | null): OnboardingSlide[] {
  const media = rubro ? CAROUSEL_MEDIA_BY_RUBRO[rubro] : undefined;
  if (!media) return ONBOARDING_SLIDES;
  return buildSlides(media, (rubro && SLIDE_COPY_BY_RUBRO[rubro]) || SLIDE_COPY);
}

/**
 * Etiqueta de rubro **como está guardada en `company_profiles.rubro`** → id del set de
 * slides. Hace falta porque el carrusel se indexa por id (`moda`, `hogar`…) pero la
 * base guarda el label de `rubroOptions` ("Moda e Indumentaria").
 *
 * Dos casos que no son 1:1:
 * - **Muebles** no tiene carrusel propio y cae en `hogar`, que es el set más cercano
 *   (ese rubro existe aparte por el modo Catálogo de muebles, ADR-585, no por diseño).
 * - Cualquier rubro sin set —incluido el texto libre de "Otro"— cae al general.
 *
 * Ver ADR-2026-08-03-catalogo-planes-usa-slides-del-rubro.
 */
const SET_POR_LABEL_DE_RUBRO: Record<string, string> = {
  'moda e indumentaria': 'moda',
  'hogar y decoracion': 'hogar',
  muebles: 'hogar',
  'belleza y cuidado personal': 'belleza',
  gastronomia: 'gastronomia',
};

/** Sin acentos y en minúsculas: el label viaja escrito a mano en varios lugares. */
function normalizarRubro(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Slides del carrusel para el rubro tal como viene de la base (el **label**). Es el
 * atajo para todo lo que está adentro de la app —el catálogo de planes, `/subscription`—,
 * donde el rubro se lee de `company_profiles` y no de la URL del onboarding.
 */
export function getCarouselSlidesPorLabel(labelRubro?: string | null): OnboardingSlide[] {
  if (!labelRubro) return ONBOARDING_SLIDES;
  return getCarouselSlides(SET_POR_LABEL_DE_RUBRO[normalizarRubro(labelRubro)]);
}

/** Copy fijo compartido por todas las slides (banner de promo, CTA, disclaimer). */
export const ONBOARDING_COPY = {
  promo: '10 créditos gratis al registrarte',
  cta: 'Comenzar a crear',
  disclaimer: 'Sin tarjeta, sin compromiso',
} as const;

/**
 * Fondo efectivo de una slide para el viewport actual (ADR-795). El set base es el de
 * **mobile**; en desktop (≥1024px) se le aplican los overrides de `slide.desktop` (imagen,
 * preview, video, videoPreview, alt), y lo que no esté definido cae al base.
 *
 * Vive acá (y no junto a los componentes de `slideMedia.tsx`) porque es una función pura
 * sobre los datos de la slide: mezclarla con exports de componentes en un `.tsx` rompe el
 * Fast Refresh de todo el módulo.
 */
export function resolveMedia(slide: OnboardingSlide, isDesktop: boolean): OnboardingSlide {
  if (!isDesktop || !slide.desktop) return slide;
  return { ...slide, ...slide.desktop };
}
